import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Company, RequestPeriod } from "../models/Company";
import { User } from "../models/User";
import { colors, buttonStyles, cardStyles, tableHeaderStyle, radius, fonts } from "../utils/colors";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  Menu as MuiMenu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { 
  Edit, 
  Business,
  People,
  CalendarMonth,
  AccessTime,
  ChevronLeft,
  Menu,
  Add,
  PersonAdd,
  MarkEmailUnread,
  Close,
  Refresh,
  Sync,
  MoreVert
} from "@mui/icons-material";
import { getCompanies, resendInvite, refreshCompany, syncCompanyRoles } from "../services/userService";
import { addUserToGroup, removeUserFromGroup } from "../services/userService";
import CreateUserDialog from "../components/CreateUserDialog";
import CreateCompanyDialog from "../components/CreateCompanyDialog";
import CreateRequestPeriodDialog from "../components/CreateRequestPeriodDialog";
import BatchImportUsersDialog from "../components/BatchImportUsersDialog";

const AdminDashboardPage: React.FC = () => {
  const { getAccessToken, isLoggedIn, userId, isAdmin, isSuperAdmin, userName } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [createCompanyDialogOpen, setCreateCompanyDialogOpen] = useState(false);
  const [createRequestPeriodDialogOpen, setCreateRequestPeriodDialogOpen] = useState(false);
  const [editingRequestPeriod, setEditingRequestPeriod] = useState<RequestPeriod | null>(null);
  const [batchImportDialogOpen, setBatchImportDialogOpen] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState<Record<string, boolean>>({});
  const [resendInviteUpdating, setResendInviteUpdating] = useState<Record<string, boolean>>({});
  const [rolesSyncing, setRolesSyncing] = useState<Record<number, boolean>>({});
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<HTMLElement | null>(null);

  // Refresh a single company in local state without reloading everything
  const refreshSingleCompany = async (companyId: number) => {
    try {
      const result = await refreshCompany(companyId, getAccessToken);
      if (result.ok && result.data) {
        setCompanies(prev =>
          prev.map(c => (c.companyId === companyId ? result.data! : c))
        );
      } else {
        // Fall back to full refresh if the partial endpoint fails
        console.warn("[AdminDashboard] Single company refresh failed, falling back to full refresh");
        await refreshCompanies();
      }
    } catch {
      await refreshCompanies();
    }
  };

  // Re-fetch every user's role/name from Entra and persist to the DB, correcting
  // any drift (e.g. from role changes made directly in the Entra portal).
  const handleSyncRoles = async (companyId: number) => {
    setRolesSyncing(prev => ({ ...prev, [companyId]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await syncCompanyRoles(companyId, getAccessToken);
      if (!result.ok || !result.data) {
        setError(result.error || "Failed to sync roles");
        return;
      }

      setCompanies(prev =>
        prev.map(c => (c.companyId === companyId ? result.data!.company : c))
      );

      const { usersUpdated, usersFailed } = result.data;
      setSuccessMessage(
        usersFailed.length > 0
          ? `Synced ${usersUpdated} user(s); failed for ${usersFailed.length}.`
          : `Synced roles for ${usersUpdated} user(s).`
      );
    } catch (err: any) {
      setError(err.message || "Failed to sync roles");
    } finally {
      setRolesSyncing(prev => ({ ...prev, [companyId]: false }));
    }
  };

  // Centralized function to refresh companies
  const refreshCompanies = async (selectFirst: boolean = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompanies(getAccessToken);
      if (result.ok && result.data) {
        setCompanies(result.data);
        if (selectFirst && result.data.length > 0) {
          setSelectedCompanyId(result.data[0].companyId);
        }
      } else {
        setError(result.error || "Failed to load companies");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    refreshCompanies(true);
  }, [isLoggedIn, userId, getAccessToken]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Auth checks
  if (!isLoggedIn) {
    return (
      <Box sx={{ maxWidth: 1200, padding: 3, paddingTop: 0 }}>
        <Alert severity="warning">Please sign in to access the admin panel.</Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 1200, margin: "40px auto", padding: 3 }}>
        <Alert severity="error">Access Denied. You must be an administrator to access this page.</Alert>
      </Box>
    );
  }

  const getResendInviteKey = (user: Company["users"][number]) => {
    return `${user.userId ?? user.email ?? user.userName ?? "unknown"}`;
  };

  const handleResendInvite = async (user: Company["users"][number]) => {
    if (!selectedCompany) {
      setError("No company selected.");
      return;
    }

    if (!user.email) {
      setError("User is missing an email address.");
      return;
    }

    const actionKey = getResendInviteKey(user);
    setResendInviteUpdating(prev => ({ ...prev, [actionKey]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await resendInvite(
        {
          email: user.email,
          displayName: user.userName || "",
          company: selectedCompany.companyName || "",
          redirectUrl: `${window.location.origin}`,
          adminUsername: userName || ""
        },
        getAccessToken
      );

      if (!result.ok) {
        setError(result.error || "Failed to resend invite");
        return;
      }

      setSuccessMessage(`Invite resent to ${user.email}.`);
    } catch (err: any) {
      setError(err.message || "Failed to resend invite");
    } finally {
      setResendInviteUpdating(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isActivePeriod = (period: RequestPeriod) => {
    const now = new Date();
    const start = new Date(period.startTime);
    const end = new Date(period.endTime);
    return now >= start && now <= end;
  };

  const getRoleKey = (entraObjectId: string | undefined, role: "user" | "admin" | "reader") => {
    return `${entraObjectId || "unknown"}-${role}`;
  };

  // True if any of this user's three role toggles has an in-flight request.
  // Role updates read-then-write against Entra, so two overlapping requests for
  // the same user (e.g. Admin and Reader toggled in quick succession) can race:
  // each computes its update from a snapshot that doesn't know about the other's
  // change, and whichever write lands last silently drops the other's role bit.
  // Disabling all three while any one is in flight forces the UI to serialize
  // them instead of firing them concurrently.
  const isUserRoleUpdating = (entraObjectId: string | undefined) => {
    const roles: Array<"user" | "admin" | "reader"> = ["user", "admin", "reader"];
    return roles.some(role => roleUpdating[getRoleKey(entraObjectId, role)]);
  };

  const getRoleGroupId = (role: "user" | "admin" | "reader", company: Company) => {
    if (role === "user") return company.userGroupObjectId;
    if (role === "admin") return company.adminGroupObjectId;
    return company.resultGroupObjectId;
  };

  const handleRoleToggle = async (
    user: Company["users"][number],
    role: "user" | "admin" | "reader",
    nextChecked: boolean
  ) => {
    const company = companies.find(c => c.companyId === selectedCompanyId);
    if (!company) return;

    const groupId = getRoleGroupId(role, company);
    const entraObjectId = user.entraObjectId;
    if (!groupId || !entraObjectId) {
      setError("Missing Entra group or user object ID.");
      return;
    }

    const roleKey = getRoleKey(entraObjectId, role);
    setRoleUpdating(prev => ({ ...prev, [roleKey]: true }));

    try {
      const request = { userId: entraObjectId, groupId, companyId: company.companyId };
      const result = nextChecked
        ? await addUserToGroup(request, getAccessToken)
        : await removeUserFromGroup(request, getAccessToken);

      if (!result.ok) {
        setError(result.error || "Failed to update role");
        return;
      }

      // Patch all role flags directly from the API response
      if (result.data) {
        const { entraAdmin, entraUser, entraReader } = result.data;
        setCompanies(prev =>
          prev.map(c =>
            c.companyId !== company.companyId
              ? c
              : {
                  ...c,
                  users: c.users.map(u =>
                    u.entraObjectId !== entraObjectId
                      ? u
                      : { ...u, entraAdmin, entraUser, entraReader }
                  )
                }
          )
        );
      } else {
        // Fallback if response doesn't include flags
        await refreshSingleCompany(company.companyId);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to update role");
    } finally {
      setRoleUpdating(prev => ({ ...prev, [roleKey]: false }));
    }
  };

  // Insert a newly created user into the correct company's user list
  const addUserToLocalState = (newUser: User) => {
    const companyId = newUser.companyId;
    if (!companyId) return;
    setCompanies(prev =>
      prev.map(c =>
        c.companyId === companyId
          ? { ...c, users: [...c.users, newUser] }
          : c
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", padding: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading companies...</Typography>
      </Box>
    );
  }

  const selectedCompany = companies.find(c => c.companyId === selectedCompanyId);

  return (
    <Box sx={{ padding: 3, paddingTop: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 3, mb: 3, flexWrap: "wrap" }}>
        {/* Company Management Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: "250px" }}>
          <Business sx={{ fontSize: 32, color: colors.darkBlue }} />
          <Box>
            <Typography variant="h5" component="h2" sx={{ color: colors.darkBlue, fontWeight: 600, fontFamily: fonts.main }}>
              Company Management
            </Typography>
            <Typography variant="body2" sx={{ color: colors.mediumGray, fontFamily: fonts.main }}>
              Manage companies, users, and request periods
            </Typography>
          </Box>
        </Box>
        <Tooltip
          title={isSuperAdmin ? "" : "Only Super Admins can create companies"}
          placement="top"
          disableHoverListener={isSuperAdmin}
        >
          <span>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateCompanyDialogOpen(true)}
              disabled={!isSuperAdmin}
              sx={buttonStyles.secondary}
            >
              New
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {companies.length === 0 ? (
        <Alert severity="info">No companies found.</Alert>
      ) : (
        <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 280px)", minHeight: "500px" }}>
          {/* Sidebar with Company List */}
          {companies.length > 1 && (
            <Paper 
              elevation={0} 
              sx={{ 
                width: sidebarCollapsed ? 60 : 280, 
                flexShrink: 0,
                overflow: "auto",
                backgroundColor: colors.white,
                borderRadius: `${radius.lg}px`,
                border: `1px solid ${colors.lightGray}`,
                transition: "width 0.3s ease"
              }}
            >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", backgroundColor: colors.offWhite, borderRadius: `${radius.lg}px ${radius.lg}px 0 0`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {!sidebarCollapsed && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Companies ({companies.length})
                </Typography>
              )}
              <IconButton
                size="small" 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{ ml: sidebarCollapsed ? 0 : 1 }}
              >
                {sidebarCollapsed ? <Menu /> : <ChevronLeft />}
              </IconButton>
            </Box>
            <Stack spacing={0}>
              {companies.map(company => (
                <Tooltip 
                  key={company.companyId}
                  title={sidebarCollapsed ? company.companyName : ""}
                  placement="right"
                  arrow
                >
                  <Box
                    onClick={() => setSelectedCompanyId(company.companyId)}
                    sx={{
                      p: sidebarCollapsed ? 1.5 : 2,
                      cursor: "pointer",
                      borderBottom: 1,
                      borderColor: "divider",
                      backgroundColor: selectedCompanyId === company.companyId ? "#ffffff" : "transparent",
                      borderLeft: selectedCompanyId === company.companyId ? 3 : 0,
                      borderLeftColor: "primary.main",
                      transition: "all 0.2s",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: sidebarCollapsed ? "center" : "flex-start",
                      "&:hover": {
                        backgroundColor: "#ffffff",
                        transform: sidebarCollapsed ? "scale(1.05)" : "translateX(2px)"
                      }
                    }}
                  >
                    {sidebarCollapsed ? (
                      <Business sx={{ fontSize: 28, color: selectedCompanyId === company.companyId ? colors.darkBlue : colors.mediumGray }} />
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, width: "100%" }}>
                          <Business sx={{ fontSize: 24, color: selectedCompanyId === company.companyId ? colors.darkBlue : colors.mediumGray }} />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: selectedCompanyId === company.companyId ? 600 : 500,
                              color: selectedCompanyId === company.companyId ? colors.darkBlue : "inherit",
                              flex: 1
                            }}
                          >
                            {company.companyName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, ml: 4 }}>
                          <Chip
                            icon={<People />}
                            label={company.users?.length || 0}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.7rem", borderColor: colors.darkGreen, color: colors.darkGreen, "& .MuiChip-icon": { color: colors.darkGreen } }}
                          />
                          <Chip
                            icon={<CalendarMonth />}
                            label={company.requestPeriod?.length || 0}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.7rem", borderColor: colors.darkGreen, color: colors.darkGreen, "& .MuiChip-icon": { color: colors.darkGreen } }}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Paper>
          )}

          {/* Main Content Area */}
          {selectedCompany && (
            <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <Card elevation={0} sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", ...cardStyles.default }}>
                <CardContent sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", p: 3 }}>
                  {/* Company Header */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ 
                        width: 44, height: 44, borderRadius: `${radius.md}px`,
                        backgroundColor: colors.yellow, display: "flex", alignItems: "center", justifyContent: "center"
                      }}>
                        <Business sx={{ fontSize: 24, color: colors.darkBlue }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: colors.darkBlue, fontFamily: fonts.main }}>
                          {selectedCompany.companyName}
                        </Typography>                     
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title="Refresh company data">
                        <IconButton
                          size="small"
                          onClick={() => refreshSingleCompany(selectedCompany.companyId)}
                          sx={{ color: colors.darkBlue }}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => setCompanyMenuAnchor(e.currentTarget)}
                          sx={{ color: colors.darkBlue }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                      <MuiMenu
                        anchorEl={companyMenuAnchor}
                        open={!!companyMenuAnchor}
                        onClose={() => setCompanyMenuAnchor(null)}
                      >
                        <MenuItem
                          disabled={!!rolesSyncing[selectedCompany.companyId]}
                          onClick={() => {
                            setCompanyMenuAnchor(null);
                            handleSyncRoles(selectedCompany.companyId);
                          }}
                        >
                          <ListItemIcon>
                            {rolesSyncing[selectedCompany.companyId]
                              ? <CircularProgress size={18} />
                              : <Sync fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText
                            primary="Update Roles for PBI"
                            secondary="Re-fetch names and roles from Entra"
                          />
                        </MenuItem>
                      </MuiMenu>
                    </Box>
                  </Box>

            
                  {/* Tabs for Request Periods and Users */}
                  <Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Tabs 
                      value={activeTab} 
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        mb: 2,
                        flexShrink: 0,
                        "& .MuiTabs-indicator": { backgroundColor: colors.darkGreen },
                        "& .MuiTab-root.Mui-selected": { color: colors.darkGreen },
                      }}
                    >
                      <Tab 
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <People />
                            Users
                            <Chip 
                              label={selectedCompany.users?.length || 0} 
                              size="small" 
                              sx={{ height: 20, fontSize: "0.7rem", backgroundColor: colors.darkGreen, color: colors.white }}
                            />
                          </Box>
                        }
                        sx={{ textTransform: "none" }}
                      />
                      <Tab 
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CalendarMonth />
                            Request Periods
                            <Chip 
                              label={selectedCompany.requestPeriod?.length || 0} 
                              size="small" 
                              sx={{ height: 20, fontSize: "0.7rem", backgroundColor: colors.darkGreen, color: colors.white }}
                            />
                          </Box>
                        }
                        sx={{ textTransform: "none" }}
                      />
                    </Tabs>

                    {/* Users Tab Content */}
                    {activeTab === 0 && (
                      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography variant="h6" sx={{ color: colors.darkBlue }}>
                            Users in {selectedCompany.companyName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            startIcon={<PersonAdd />}
                            size="small"
                            onClick={() => setCreateUserDialogOpen(true)}
                            sx={buttonStyles.secondary}
                          >
                            Invite User
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<People />}
                            size="small"
                            onClick={() => setBatchImportDialogOpen(true)}
                            sx={buttonStyles.secondary}
                          >
                            Batch Import
                          </Button>
                          </Box>
                        </Box>
                        {(!selectedCompany.users || selectedCompany.users.length === 0) ? (
                          <Alert severity="info">No users in this company.</Alert>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={tableHeaderStyle}>ID</TableCell>
                                  <TableCell sx={tableHeaderStyle}>Name</TableCell>
                                  <TableCell sx={tableHeaderStyle}>Email</TableCell>
                                  <TableCell sx={tableHeaderStyle}>Entra Object ID</TableCell>
                                  <TableCell sx={{ ...tableHeaderStyle, textAlign: "center" }}>Status</TableCell>
                                  <TableCell sx={{ ...tableHeaderStyle, textAlign: "center" }}>User</TableCell>
                                  <TableCell sx={{ ...tableHeaderStyle, textAlign: "center" }}>Admin</TableCell>
                                  <TableCell sx={{ ...tableHeaderStyle, textAlign: "center" }}>Reader</TableCell>
                                  <TableCell sx={{ ...tableHeaderStyle, textAlign: "right" }}>Resend invite</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedCompany.users.map(user => (
                                  <TableRow
                                    key={user.userId}
                                    sx={{ "&:hover": { backgroundColor: colors.offWhite }, transition: "background-color 0.15s ease" }}
                                  >
                                    <TableCell>{user.userId}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{user.userName}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem", color: colors.mediumGray, fontFamily: fonts.mono }}>
                                      {user.entraObjectId}
                                    </TableCell>
                                    <TableCell align="center">
                                      {user.status === "submitted" && (
                                        <Tooltip title="Submitted for current period">
                                          <Chip label="Submitted" size="small" color="success" variant="filled" />
                                        </Tooltip>
                                      )}
                                      {user.status === "startedDraft" && (
                                        <Tooltip title="Draft in progress, not yet submitted">
                                          <Chip label="Draft" size="small" color="warning" variant="filled" />
                                        </Tooltip>
                                      )}
                                      {user.status === "notStarted" && (
                                        <Tooltip title="Not started">
                                          <Chip label="Not started" size="small" color="default" variant="outlined" />
                                        </Tooltip>
                                      )}
                                      {user.status === "noActivePeriod" && (
                                        <Tooltip title="No active request period">
                                          <Chip label="N/A" size="small" color="default" variant="outlined" sx={{ opacity: 0.5 }} />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title={user.entraUser ? "User role enabled" : "User role disabled"}>
                                        <Switch
                                          size="small"
                                          checked={!!user.entraUser}
                                          onChange={(e) => handleRoleToggle(user, "user", e.target.checked)}
                                          disabled={
                                            !selectedCompany?.userGroupObjectId ||
                                            !user.entraObjectId ||
                                            isUserRoleUpdating(user.entraObjectId) ||
                                            !!rolesSyncing[selectedCompany.companyId]
                                          }
                                          color="success"
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title={user.entraAdmin ? "Admin role enabled" : "Admin role disabled"}>
                                        <Switch
                                          size="small"
                                          checked={!!user.entraAdmin}
                                          onChange={(e) => handleRoleToggle(user, "admin", e.target.checked)}
                                          disabled={
                                            !selectedCompany?.adminGroupObjectId ||
                                            !user.entraObjectId ||
                                            isUserRoleUpdating(user.entraObjectId) ||
                                            !!rolesSyncing[selectedCompany.companyId]
                                          }
                                          color="success"
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title={user.entraReader ? "Reader role enabled" : "Reader role disabled"}>
                                        <Switch
                                          size="small"
                                          checked={!!user.entraReader}
                                          onChange={(e) => handleRoleToggle(user, "reader", e.target.checked)}
                                          disabled={
                                            !selectedCompany?.resultGroupObjectId ||
                                            !user.entraObjectId ||
                                            isUserRoleUpdating(user.entraObjectId) ||
                                            !!rolesSyncing[selectedCompany.companyId]
                                          }
                                          color="success"
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title="Resend invite">
                                        <span>
                                          <IconButton
                                            size="small"
                                            sx={{ color: colors.darkGreen }}
                                            disabled={!user.email || resendInviteUpdating[getResendInviteKey(user)]}
                                            onClick={() => handleResendInvite(user)}
                                          >
                                            <MarkEmailUnread fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    )}

                    {/* Request Periods Tab Content */}
                    {activeTab === 1 && (
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography variant="h6" sx={{ color: colors.darkBlue }}>
                            Request Periods for {selectedCompany.companyName}
                          </Typography>
                          <Tooltip
                            title={isSuperAdmin ? "" : "Only Super Admins can manage request periods"}
                            placement="top"
                            disableHoverListener={isSuperAdmin}
                          >
                            <span>
                              <Button
                                variant="outlined"
                                startIcon={<Add />}
                                size="small"
                                onClick={() => setCreateRequestPeriodDialogOpen(true)}
                                disabled={!isSuperAdmin}
                                sx={buttonStyles.secondary}
                              >
                                Add Period
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                        {(!selectedCompany.requestPeriod || selectedCompany.requestPeriod.length === 0) ? (
                          <Alert severity="info">No request periods for this company.</Alert>
                        ) : (
                          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {selectedCompany.requestPeriod.map(period => (
                          <Box key={period.requestPeriodId} sx={{ flex: "1 1 300px", minWidth: "150px", maxWidth: "250px" }}>
                            <Paper 
                              elevation={0} 
                              sx={{ 
                                p: 2, 
                                borderRadius: `${radius.lg}px`,
                                border: `1px solid ${colors.lightGray}`,
                                backgroundColor: isActivePeriod(period) ? colors.yellow : colors.white,
                                transition: "all 0.2s ease",
                                "&:hover": { boxShadow: "0 2px 8px rgba(13,44,64,0.1)" },
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {period.periodName}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  {isActivePeriod(period) && (
                                    <Chip label="Active" size="small" color="success" />
                                  )}
                                  {isSuperAdmin && (
                                  <Tooltip title="Edit request period">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => {
                                        setEditingRequestPeriod(period);
                                        setCreateRequestPeriodDialogOpen(true);
                                      }}
                                    >
                                      <Edit sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <AccessTime sx={{ fontSize: 16, color: colors.mediumGray }} />
                                  <Typography variant="caption" sx={{ color: colors.mediumGray, fontFamily: fonts.main }}>
                                    Start: {formatDate(period.startTime)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <AccessTime sx={{ fontSize: 16, color: colors.mediumGray }} />
                                  <Typography variant="caption" sx={{ color: colors.mediumGray, fontFamily: fonts.main }}>
                                    End: {formatDate(period.endTime)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Box>
                        ))}
                      </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      )}

      {/* Create User Dialog */}
      <Dialog 
        open={createUserDialogOpen} 
        onClose={() => setCreateUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${radius.lg}px` } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Invite User to {selectedCompany?.companyName}
          <IconButton size="small" onClick={() => setCreateUserDialogOpen(false)} sx={{ color: colors.mediumGray }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateUserDialog 
            preSelectedCompanyId={selectedCompanyId || undefined}
            companies={companies}
            onSuccess={async (createdUser?: User) => {
              setCreateUserDialogOpen(false);
              if (createdUser?.companyId) {
                // Insert directly — no API call needed
                addUserToLocalState(createdUser);
              } else if (selectedCompanyId) {
                // Fallback if dialog doesn't pass user back
                await refreshSingleCompany(selectedCompanyId);
              } else {
                await refreshCompanies();
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Batch Import Users Dialog */}
      <Dialog
        open={batchImportDialogOpen}
        onClose={() => setBatchImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${radius.lg}px` } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Batch Import Users to {selectedCompany?.companyName}
          <IconButton size="small" onClick={() => setBatchImportDialogOpen(false)} sx={{ color: colors.mediumGray }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BatchImportUsersDialog
            preSelectedCompanyId={selectedCompanyId || undefined}
            companies={companies}
            onSuccess={async (createdUsers?: User[]) => {
              setBatchImportDialogOpen(false);
              if (createdUsers && createdUsers.length > 0) {
                // Insert all created users into local state — no API calls
                setCompanies(prev =>
                  prev.map(c => {
                    const usersForCompany = createdUsers.filter(u => u.companyId === c.companyId);
                    if (usersForCompany.length === 0) return c;
                    return { ...c, users: [...c.users, ...usersForCompany] };
                  })
                );
              } else if (selectedCompanyId) {
                await refreshSingleCompany(selectedCompanyId);
              } else {
                await refreshCompanies();
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog 
        open={createCompanyDialogOpen} 
        onClose={() => setCreateCompanyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${radius.lg}px` } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Create New Company
          <IconButton size="small" onClick={() => setCreateCompanyDialogOpen(false)} sx={{ color: colors.mediumGray }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateCompanyDialog 
            companies={companies}
            onSuccess={async () => {
              setCreateCompanyDialogOpen(false);
              await refreshCompanies();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create/Edit Request Period Dialog */}
      <Dialog 
        open={createRequestPeriodDialogOpen} 
        onClose={() => {
          setCreateRequestPeriodDialogOpen(false);
          setEditingRequestPeriod(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${radius.lg}px` } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editingRequestPeriod ? 'Edit Request Period' : 'Create Request Period'}
          <IconButton size="small" onClick={() => { setCreateRequestPeriodDialogOpen(false); setEditingRequestPeriod(null); }} sx={{ color: colors.mediumGray }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <CreateRequestPeriodDialog 
              companyId={selectedCompany.companyId}
              companyName={selectedCompany.companyName}
              requestPeriod={editingRequestPeriod ? {
                id: editingRequestPeriod.requestPeriodId,
                periodName: editingRequestPeriod.periodName,
                startTime: editingRequestPeriod.startTime,
                endTime: editingRequestPeriod.endTime
              } : undefined}
              onSuccess={async (period, isUpdate) => {
                setCreateRequestPeriodDialogOpen(false);
                setEditingRequestPeriod(null);
                if (period) {
                  setCompanies(prev =>
                    prev.map(c =>
                      c.companyId !== selectedCompany.companyId
                        ? c
                        : {
                            ...c,
                            requestPeriod: isUpdate
                              ? c.requestPeriod.map(p =>
                                  p.requestPeriodId === period.requestPeriodId ? period : p
                                )
                              : [...(c.requestPeriod || []), period]
                          }
                    )
                  );
                } else {
                  await refreshSingleCompany(selectedCompany.companyId);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminDashboardPage;
